import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import {
  PlannerBlock, FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";
import InternalRail from "../components/site/InternalRail";
import { suburbsForCuisine, blogsForCuisine } from "../data/internalLinks";
import { CUISINE_BY_SLUG, CUISINES } from "../data/cuisines";

export default function CuisinePage() {
  const { cuisine } = useParams();
  const data = CUISINE_BY_SLUG[cuisine];
  if (!data) return <Navigate to="/" replace />;

  const others = CUISINES.filter((c) => c.slug !== data.slug).slice(0, 4);

  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />

      {/* Hero */}
      <section className="relative min-h-[80svh] overflow-hidden bg-ink">
        <div className="absolute inset-0">
          <Photo slug={data.hero} alt={`${data.name} photographer Melbourne`} priority ratio="auto" className="!relative h-full w-full" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/45 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[80svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
          <Breadcrumb current={`${data.name} · Photography`} />
          <div className="flex-1 flex flex-col justify-center max-w-5xl">
            <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
              {data.eyebrow}
            </p>
            <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7vw, 6rem)" }}>
              {data.h1Top}
              <br />
              <span className="italic font-light text-ember">{data.h1Bot}</span>
            </h1>
            <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
              {data.intro}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/get-a-quote"><CTAButton variant="primary" size="lg">Get {/^[aeiou]/i.test(data.short) ? "an" : "a"} {data.short} Quote</CTAButton></Link>
              <a href="tel:0494756111"><CTAButton variant="ghost" size="lg" arrow={false}>Call 0494 756 111</CTAButton></a>
            </div>
          </div>
        </div>
      </section>

      {/* What we shoot */}
      <section className="relative bg-ink text-bone py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-12 items-end">
            <div className="col-span-12 lg:col-span-7">
              <p className="text-xs uppercase tracking-wider2 text-ember mb-5 font-bold">What we shoot · {data.short}</p>
              <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}>
                Four shot types
                <br />
                <span className="italic font-light text-ember">that move the menu.</span>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:pb-3">
              <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
                {data.brief}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
            {data.dining.map((d, i) => (
              <div key={i} className="bg-ink p-7 md:p-8">
                <span className="font-display italic text-2xl text-ember leading-none">0{i + 1}</span>
                <p className="mt-4 font-display text-base md:text-lg tracking-editorial leading-tight">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PlannerBlock
        eyebrow={`Pricing · ${data.name}`}
        headline="Scope your shoot"
        italic={`for ${data.short.toLowerCase()}.`}
        blurb={`The planner below tiers down per-image as the menu grows — exactly the way we quote ${data.short.toLowerCase()} briefs. No travel fees across metro Melbourne.`}
      />

      {/* Other cuisines */}
      <section className="bg-bone text-ink py-20 lg:py-28">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <p className="text-xs uppercase tracking-wider2 text-ember mb-5 font-bold">Also offered · Cuisine specialists</p>
          <h2 className="font-display tracking-editorial leading-[0.95] mb-10" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
            Other cuisines
            <span className="italic font-light text-ember"> we shoot for.</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
            {others.map((c) => (
              <Link
                key={c.slug}
                to={`/cuisine/${c.slug}`}
                data-testid={`cuisine-${c.slug}`}
                className="group bg-bone hover:bg-ink hover:text-bone transition-colors p-6 flex flex-col"
              >
                <Photo slug={c.hero} alt={`${c.name} photographer Melbourne`} ratio="4/5" sizes="(min-width: 1024px) 22vw, 50vw" />
                <p className="mt-4 font-display text-lg md:text-xl tracking-editorial leading-tight group-hover:text-ember transition-colors">
                  {c.name}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <SisterServices keys={["menu", "food", "ubereats", "brand"]} />
      <InternalRail
        eyebrow={`Where we shoot · ${data.short}`}
        headline={`Suburbs known for`}
        italic={`${data.short.toLowerCase()}.`}
        theme="bone"
        cols={4}
        items={suburbsForCuisine(data.slug)}
      />
      <InternalRail
        eyebrow="Related reading · The Journal"
        headline="More on"
        italic={`${data.short.toLowerCase()}.`}
        theme="ink"
        cols={3}
        items={blogsForCuisine(data.slug)}
      />
      <FAQSection
        eyebrow={`Common questions · ${data.name}`}
        headline="Brief, scope,"
        italic="and what's included."
        intro={`Most ${data.short.toLowerCase()} briefs are quoted in under five minutes by phone.`}
        faqs={data.faqs}
      />
      <FinalCTA
        eyebrow={`Final step · ${data.short} specialists`}
        headline="Ready to shoot"
        italic={`your ${data.short.toLowerCase()} menu?`}
        intro="One call, one quote, one shoot day."
        backdropSlug={data.hero}
      />
      <Footer />
    </div>
  );
}
